import { MoonfallConsent } from './consent-state.js';
import { replayInputText, startReplayContext } from './replay-recording.js';
/**
 * Consent-gated PostHog loader (EU-hosted).
 *
 * Hard guarantee: the PostHog library is NOT loaded and NO network request is made until
 * analytics consent is granted (and GPC is absent). The official snippet stub is only
 * injected inside enable(); init() — which fetches array.js — only runs on consent.
 *
 * Reacts live to consent changes (no page reload needed) and to login (identify with the
 * Convex userId so server- and client-side events stitch to one person).
 */
let MoonfallAnalytics;

(function () {
  "use strict";

  var initialized = false;
  var pendingUserId = null;
  // Listeners for the replay's session id (MoonfallAnalytics.onReplayIds).
  var replayListeners = [];
  var sessionHookAttached = false;

  function currentReplayIds() {
    var ph = window.posthog;
    if (!initialized || !ph || typeof ph.get_session_id !== "function") return null;
    if (typeof ph.has_opted_out_capturing === "function" && ph.has_opted_out_capturing()) return null;
    var sessionId = ph.get_session_id();
    // Which replay, nothing more: the server knows who the player is.
    return sessionId ? { sessionId: sessionId, windowId: null } : null;
  }

  function notifyReplayListeners() {
    var ids = currentReplayIds();
    if (!ids) return;
    replayListeners.forEach(function (listener) {
      try { listener(ids); } catch (_) { /* one listener's failure is its own */ }
    });
  }

  // posthog-js calls back when the session id is first known and whenever it
  // rotates; through the stub, the hook waits for the library to load.
  function attachSessionHook() {
    if (sessionHookAttached || !initialized || !window.posthog || typeof window.posthog.onSessionId !== "function") return;
    sessionHookAttached = true;
    window.posthog.onSessionId(notifyReplayListeners);
  }

  function cfg() {
    var c = (window.__MOONFALL_CONFIG && window.__MOONFALL_CONFIG.posthog) || {};
    return { key: c.key || "", host: c.host || "https://eu.i.posthog.com" };
  }

  // Official posthog-js snippet (array stub). Defining it makes no network call; the
  // library (array.js) is fetched only when posthog.init() runs.
  function defineStub() {
    if (window.posthog && window.posthog.__SV) return;
    !function (t, e) { var o, n, p, r; e.__SV || (window.posthog = e, e._i = [], e.init = function (i, s, a) { function g(t, e) { var o = e.split("."); 2 == o.length && (t = t[o[0]], e = o[1]), t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))) } } (p = t.createElement("script")).type = "text/javascript", p.crossOrigin = "anonymous", p.async = !0, p.src = s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js", (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r); var u = e; for (void 0 !== a ? u = e[a] = [] : a = "posthog", u.people = u.people || [], u.toString = function (t) { var e = "posthog"; return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e }, u.people.toString = function () { return u.toString(1) + ".people (stub)" }, o = "init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "), n = 0; n < o.length; n++) g(u, o[n]); e._i.push([i, s, a]) }, e.__SV = 1) }(document, window.posthog || []);
  }

  function enable() {
    var c = cfg();
    if (!c.key) return; // no key => analytics disabled (visible no-op)
    if (initialized) {
      window.posthog.opt_in_capturing();
    } else {
      defineStub();
      // First-party reverse proxy: the server forwards /ingest/* to PostHog EU, so ad/tracker
      // blockers (which block *.posthog.com) can't drop events. ui_host points dashboard/toolbar
      // links at the real PostHog app.
      var apiHost = window.location.origin + "/ingest";
      var uiHost = (c.host || "https://eu.i.posthog.com").replace(".i.posthog.com", ".posthog.com");
      window.posthog.init(c.key, {
        api_host: apiHost,
        ui_host: uiHost,
        person_profiles: "identified_only", // cheaper + less data for anonymous visitors
        autocapture: true,
        capture_pageview: true,
        // The marketing landing scrolls inside #deck, not the document (see
        // docs/mobile-page-box.md), so document scroll depth there is always 0.
        // Point PostHog at the real scroller; the first selector that matches
        // wins, and every other page falls through to the document as before.
        // This changes only where an already-collected metric is measured — no
        // new data is captured.
        scroll_root_selector: ["#deck", "body", "html"],
        disable_session_recording: false,
        session_recording: {
          // Every field goes through replayInputText: typing in the game's
          // own fields is shown, everything else stays masked
          // (replay-recording.js).
          maskAllInputs: true,
          maskInputFn: replayInputText,
          // The card tilt restyles the hovered card once per animation frame.
          // PostHog's default throttle (10 changes a second after a burst of
          // 100) dropped the change that ends a hover, so replays showed cards
          // stuck hovered (2026-10-18). One change per frame up to 240 Hz now
          // passes; anything faster is still throttled.
          __mutationThrottlerRefillRate: 250,
          __mutationThrottlerBucketSize: 500,
        },
        // Browser errors go to Error Tracking and onto the replay timeline.
        capture_exceptions: true,
        // The page's own requests carry the replay's session id
        // (X-POSTHOG-SESSION-ID), so the server can put its failures on the same
        // replay (game/utils/requestContext.js). This host only.
        tracing_headers: [window.location.hostname],
        opt_out_capturing_by_default: false,
      });
      initialized = true;
      // The notch margins, keyboard and touch input a replay cannot see.
      startReplayContext({
        capture: function (event, props) { window.posthog.capture(event, props); },
        isActive: function () {
          var ph = window.posthog;
          return initialized && !(ph && typeof ph.has_opted_out_capturing === "function" && ph.has_opted_out_capturing());
        },
      });
    }
    if (pendingUserId) {
      window.posthog.identify(pendingUserId);
      pendingUserId = null;
    }
    if (replayListeners.length) attachSessionHook();
  }

  function disable() {
    if (initialized && window.posthog && window.posthog.opt_out_capturing) {
      window.posthog.opt_out_capturing();
    }
  }

  function sync(allowed) {
    if (allowed) enable();
    else disable();
  }

  // Public API for the auth/consent flow.
  MoonfallAnalytics = {
    enable: enable,
    disable: disable,
    isEnabled: function () {
      return initialized;
    },
    // Call on login with the Convex userId (+ optional person properties).
    identify: function (userId, props) {
      if (!userId) return;
      if (initialized && window.posthog) window.posthog.identify(userId, props || {});
      else pendingUserId = userId; // applied when consent enables capture
    },
    // Capture a custom event (no-op until consent enables analytics).
    capture: function (event, props) {
      if (initialized && window.posthog && window.posthog.capture) window.posthog.capture(event, props || {});
    },
    // The replay this page records, so a server failure in a match can land on
    // it (socket-handler.js). Nothing before consent or after withdrawal.
    replayIds: currentReplayIds,
    // Calls back with the replay ids once PostHog knows them and whenever they
    // change, including when consent is granted later.
    onReplayIds: function (listener) {
      if (typeof listener !== "function") return;
      replayListeners.push(listener);
      attachSessionHook();
      var ids = currentReplayIds();
      if (ids) {
        try { listener(ids); } catch (_) { /* the listener's failure is its own */ }
      }
    },
  };

  // Apply initial state + react to future consent changes.
  if (MoonfallConsent) {
    MoonfallConsent.onChange(sync);
    if (MoonfallConsent.analyticsAllowed()) enable();
  }
})();

export { MoonfallAnalytics };
